import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import AppLayout from "@/components/AppLayout";
import VideoLibrary from "@/components/VideoLibrary";
import { Play, Trophy, Clock, Target, Sparkles, History, Flame, Award, Film, Timer, X, AlertTriangle, FolderOpen } from "lucide-react";
import { BadgePill } from "@/lib/badges";

function fmtTime(s) {
  if (s == null) return "—";
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return `${m}m ${sec}s`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
  } catch { return iso; }
}

export default function StudentDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [assignments, setAssignments] = useState([]);
  const [history, setHistory] = useState([]);
  const [gamification, setGamification] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(null);
  const [assessmentIntro, setAssessmentIntro] = useState(null);
  const [tab, setTab] = useState("practice");
  const levels = [...new Set(assignments.map((a) => a.level))].sort((x, y) => x - y);

  const beginAssessment = async () => {
    if (!assessmentIntro) return;
    setStarting(assessmentIntro.exercise_id);
    try {
      const { data } = await api.post("/student/attempts", { exercise_id: assessmentIntro.exercise_id });
      setAssessmentIntro(null);
      navigate(`/practice/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setStarting(null);
    }
  };

  useEffect(() => {
    (async () => {
      try {
        const [a, h, g] = await Promise.all([
          api.get("/student/assignments"),
          api.get("/student/history"),
          api.get("/gamification/me"),
        ]);
        setAssignments(a.data || []);
        setHistory(h.data || []);
        setGamification(g.data);
      } catch (err) {
        toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const startPractice = async (a) => {
    if (a.type === "assessment") {
      if ((a.attempts_remaining ?? 3) <= 0) {
        toast.error("You've used all attempts for this assessment.");
        return;
      }
      setAssessmentIntro(a);
      return;
    }
    setStarting(a.exercise_id);
    try {
      const { data } = await api.post("/student/attempts", { exercise_id: a.exercise_id });
      navigate(`/practice/${data.id}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setStarting(null);
    }
  };

  return (
    <AppLayout variant="student">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        {/* Greeting */}
        <section className="mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-lemon/40 text-ink font-bold text-sm mb-3">
            <Sparkles className="w-4 h-4" /> Ready to practice?
          </div>
          <h1 data-testid="student-greeting" className="font-heading text-4xl sm:text-5xl font-bold text-ink">
            Hi {user?.name?.split(" ")[0] || "there"}! 👋
          </h1>
          <p className="text-lg text-stone mt-2">Pick an exercise below and beat your best score.</p>
        </section>

        {/* Gamification */}
        {gamification && (
          <section className="mb-10" data-testid="gamification-section">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-4">
              <div className="p-5 bg-white rounded-3xl card-tactile flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-watermelon text-white flex items-center justify-center"><Flame className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-pebble">Streak</div>
                  <div data-testid="stat-streak" className="font-heading text-2xl font-bold text-ink">{gamification.streak_days} day{gamification.streak_days === 1 ? "" : "s"}</div>
                </div>
              </div>
              <div className="p-5 bg-white rounded-3xl card-tactile flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-sky text-white flex items-center justify-center"><Trophy className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-pebble">Points</div>
                  <div data-testid="stat-points" className="font-heading text-2xl font-bold text-ink">{gamification.points}</div>
                </div>
              </div>
              <div className="p-5 bg-white rounded-3xl card-tactile flex items-center gap-3">
                <div className="w-11 h-11 rounded-2xl bg-leaf text-white flex items-center justify-center"><Target className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-pebble">Correct</div>
                  <div data-testid="stat-correct" className="font-heading text-2xl font-bold text-ink">{gamification.total_correct}</div>
                </div>
              </div>
              <Link to="/student/leaderboard" data-testid="leaderboard-link" className="p-5 bg-lemon rounded-3xl card-tactile flex items-center gap-3 hover:-translate-y-1 transition-transform">
                <div className="w-11 h-11 rounded-2xl bg-ink text-white flex items-center justify-center"><Award className="w-5 h-5" /></div>
                <div>
                  <div className="text-xs font-bold uppercase tracking-wider text-ink/70">Leaderboard</div>
                  <div className="font-heading text-xl font-bold text-ink">View ranks →</div>
                </div>
              </Link>
            </div>
            {gamification.badges.length > 0 && (
              <div className="flex flex-wrap gap-2" data-testid="badges-list">
                {gamification.badges.map((b) => <BadgePill key={b} id={b} />)}
              </div>
            )}
          </section>
        )}

        {/* Tabs */}
        <div className="flex items-center gap-2 mb-8" data-testid="student-dashboard-tabs">
          <button
            data-testid="tab-practice"
            onClick={() => setTab("practice")}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-colors ${tab === "practice" ? "bg-sky text-white shadow-[0_3px_0_0_rgba(0,0,0,0.12)]" : "bg-white border-2 border-cloud text-stone hover:border-sky hover:text-sky-hover"}`}
          >
            <Target className="w-4 h-4" /> Practice
          </button>
          <button
            data-testid="tab-learning-videos"
            onClick={() => setTab("videos")}
            className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full font-bold text-sm transition-colors ${tab === "videos" ? "bg-sky text-white shadow-[0_3px_0_0_rgba(0,0,0,0.12)]" : "bg-white border-2 border-cloud text-stone hover:border-sky hover:text-sky-hover"}`}
          >
            <Film className="w-4 h-4" /> Learning Videos
          </button>
        </div>

        {tab === "practice" && (
        <>
        {/* Assignments */}
        <section className="mb-14">
          <div className="flex items-baseline justify-between mb-5">
            <h2 className="font-heading text-2xl font-bold text-ink">Your exercises</h2>
            <span className="text-sm font-semibold text-stone">{assignments.length} assigned</span>
          </div>
          {loading ? (
            <div data-testid="assignments-loading" className="text-stone">Loading…</div>
          ) : assignments.length === 0 ? (
            <div data-testid="assignments-empty" className="p-8 bg-white rounded-3xl card-tactile text-center text-stone">
              No exercises assigned yet. Please check back later or ask your teacher.
            </div>
          ) : (
            <div className="space-y-8">
              {levels.map((lvl) => (
                <div key={lvl} data-testid={`level-folder-${lvl}`} className="rounded-3xl border-2 border-cloud bg-white/60 overflow-hidden">
                  <div className="flex items-center gap-2 px-5 py-3 bg-sky-light/60 border-b-2 border-cloud">
                    <FolderOpen className="w-5 h-5 text-sky-hover" />
                    <span className="font-heading text-lg font-bold text-ink">Level {lvl}</span>
                    <span className="text-xs font-bold text-stone">{assignments.filter((a) => a.level === lvl).length} exercise(s)</span>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-5">
              {assignments.filter((a) => a.level === lvl).map((a) => {
                const isAssessment = a.type === "assessment";
                const attemptsExhausted = isAssessment && (a.attempts_remaining ?? 3) <= 0;
                return (
                <div
                  key={a.assignment_id}
                  data-testid={`assignment-card-${a.exercise_id}`}
                  className={`p-6 bg-white rounded-3xl card-tactile flex flex-col hover:-translate-y-1 transition-transform ${isAssessment ? "border-2 border-lemon" : ""}`}
                >
                  <div className="flex items-start justify-between mb-3">
                    <div className="inline-flex items-center gap-2 flex-wrap">
                      {isAssessment && (
                        <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-lemon text-ink text-xs font-bold">
                          <Timer className="w-3 h-3" /> Timed exam
                        </span>
                      )}
                      <span className="px-3 py-1 rounded-full bg-sky-light text-sky-hover text-xs font-bold">Level {a.level}</span>
                      <span className="px-3 py-1 rounded-full bg-canvas text-stone text-xs font-bold">Week {a.week}</span>
                    </div>
                    <span
                      className={`px-3 py-1 rounded-full text-xs font-bold ${
                        a.status === "Completed" ? "bg-leaf/15 text-leaf" : "bg-lemon/30 text-ink"
                      }`}
                    >
                      {a.status}
                    </span>
                  </div>
                  <h3 className="font-heading text-2xl font-bold text-ink leading-tight mb-1">{a.title}</h3>
                  <p className="text-sm text-stone mb-4">{a.topic}</p>
                  <div className="grid grid-cols-2 gap-3 mb-6">
                    <div className="p-3 rounded-2xl bg-canvas">
                      <div className="text-xs font-bold uppercase tracking-wider text-pebble">Questions</div>
                      <div className="font-heading font-bold text-2xl text-ink">{a.question_count}</div>
                    </div>
                    <div className="p-3 rounded-2xl bg-canvas">
                      <div className="text-xs font-bold uppercase tracking-wider text-pebble">
                        {isAssessment ? "Attempts left" : "Best Score"}
                      </div>
                      <div className="font-heading font-bold text-2xl text-ink">
                        {isAssessment
                          ? `${a.attempts_remaining ?? 3}/${a.max_attempts ?? 3}`
                          : (a.best_score != null ? `${a.best_score}/${a.best_total}` : "—")}
                      </div>
                    </div>
                  </div>
                  {isAssessment && a.best_score != null && (
                    <div className="mb-4 p-3 rounded-2xl bg-lemon/25 text-sm">
                      <span className="text-stone">Best so far: </span>
                      <span className="font-heading font-bold text-ink">{a.best_score}/{a.best_total}</span>
                    </div>
                  )}
                  <div className="mt-auto space-y-2">
                    {a.lesson && (
                      <Link
                        to="/student/videos"
                        data-testid={`watch-lesson-${a.exercise_id}`}
                        className="inline-flex items-center justify-center gap-2 w-full h-11 rounded-full bg-sky-light text-sky-hover font-bold text-sm btn-tactile hover:bg-sky hover:text-white"
                      >
                        <Film className="w-4 h-4" /> Watch lesson: {a.lesson.title}
                      </Link>
                    )}
                    <button
                      data-testid={`start-practice-${a.exercise_id}`}
                      onClick={() => startPractice(a)}
                      disabled={starting === a.exercise_id || attemptsExhausted}
                      className="w-full inline-flex items-center justify-center gap-2 h-12 rounded-full bg-watermelon text-white font-heading font-bold text-base btn-tactile hover:bg-watermelon-hover disabled:opacity-70"
                    >
                      {isAssessment ? <Timer className="w-5 h-5" /> : <Play className="w-5 h-5" fill="currentColor" />}
                      {starting === a.exercise_id
                        ? "Starting…"
                        : attemptsExhausted
                        ? "No attempts left"
                        : isAssessment
                        ? "Start Exam"
                        : "Start Practice"}
                    </button>
                  </div>
                </div>
              );})}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>

        {/* History */}
        <section>
          <div className="flex items-center gap-2 mb-5">
            <History className="w-6 h-6 text-sky" />
            <h2 className="font-heading text-2xl font-bold text-ink">Practice history</h2>
          </div>
          <div className="bg-white rounded-3xl card-tactile overflow-hidden">
            {loading ? (
              <div data-testid="history-loading" className="p-8 text-center text-stone">Loading…</div>
            ) : history.length === 0 ? (
              <div data-testid="history-empty" className="p-8 text-center text-stone">You haven't completed any exercises yet.</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-canvas">
                    <tr className="text-left text-xs uppercase tracking-wider text-stone">
                      <th className="p-4 font-bold">Exercise</th>
                      <th className="p-4 font-bold">Date</th>
                      <th className="p-4 font-bold">Score</th>
                      <th className="p-4 font-bold">Accuracy</th>
                      <th className="p-4 font-bold">Time</th>
                    </tr>
                  </thead>
                  <tbody data-testid="history-table" className="divide-y divide-cloud">
                    {history.map((h) => (
                      <tr key={h.id} className="hover:bg-canvas/50">
                        <td className="p-4 font-semibold text-ink">{h.exercise_title}</td>
                        <td className="p-4 text-stone">{fmtDate(h.submitted_at)}</td>
                        <td className="p-4"><span className="inline-flex items-center gap-1 font-heading font-bold text-ink"><Trophy className="w-4 h-4 text-lemon" />{h.score}/{h.total_questions}</span></td>
                        <td className="p-4"><span className="inline-flex items-center gap-1 font-semibold text-leaf"><Target className="w-4 h-4" />{h.accuracy}%</span></td>
                        <td className="p-4"><span className="inline-flex items-center gap-1 text-stone"><Clock className="w-4 h-4" />{fmtTime(h.time_taken_seconds)}</span></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
        </>
        )}

        {tab === "videos" && (
          <section className="mb-14" data-testid="learning-videos-tab-content">
            <div className="flex items-center gap-2 mb-5">
              <Film className="w-6 h-6 text-sky" />
              <h2 className="font-heading text-2xl font-bold text-ink">Learning videos</h2>
            </div>
            <VideoLibrary />
          </section>
        )}

        {/* Assessment intro modal */}
        {assessmentIntro && (
          <div className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-sm overflow-y-auto" onClick={() => !starting && setAssessmentIntro(null)}>
            <div className="min-h-full flex items-end sm:items-center justify-center p-4">
              <div
                data-testid="assessment-intro-modal"
                className="w-full max-w-md bg-white rounded-3xl p-6 shadow-2xl my-8"
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded-2xl bg-lemon flex items-center justify-center">
                      <Timer className="w-6 h-6 text-ink" />
                    </div>
                    <div>
                      <div className="inline-flex items-center gap-2 px-2 py-0.5 rounded-full bg-lemon/40 text-ink text-xs font-bold mb-1">Timed exam</div>
                      <h2 className="font-heading text-2xl font-bold text-ink leading-tight">{assessmentIntro.title}</h2>
                    </div>
                  </div>
                  <button onClick={() => setAssessmentIntro(null)} disabled={!!starting} className="text-stone hover:text-ink">
                    <X className="w-6 h-6" />
                  </button>
                </div>

                <div className="grid grid-cols-3 gap-2 mb-4">
                  <div className="p-3 rounded-2xl bg-canvas text-center">
                    <div className="text-xs font-bold uppercase text-pebble">Questions</div>
                    <div data-testid="intro-question-count" className="font-heading text-2xl font-bold text-ink">{assessmentIntro.question_count}</div>
                  </div>
                  <div className="p-3 rounded-2xl bg-canvas text-center">
                    <div className="text-xs font-bold uppercase text-pebble">Sec / Q</div>
                    <div data-testid="intro-seconds" className="font-heading text-2xl font-bold text-ink">{assessmentIntro.seconds_per_question}</div>
                  </div>
                  <div className="p-3 rounded-2xl bg-canvas text-center">
                    <div className="text-xs font-bold uppercase text-pebble">Attempts</div>
                    <div data-testid="intro-attempts" className="font-heading text-2xl font-bold text-ink">{assessmentIntro.attempts_remaining}<span className="text-sm text-stone">/{assessmentIntro.max_attempts}</span></div>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-lemon/20 border-2 border-lemon mb-4 text-sm text-ink space-y-1.5">
                  <div className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 text-watermelon mt-0.5 shrink-0" /> <span>Each question has a countdown. When it hits 0, it auto-skips.</span></div>
                  <div className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 text-watermelon mt-0.5 shrink-0" /> <span>Skipped questions <strong>don't hurt your accuracy</strong>.</span></div>
                  <div className="flex items-start gap-2"><AlertTriangle className="w-4 h-4 text-watermelon mt-0.5 shrink-0" /> <span>You'll see the correct answers only after all {assessmentIntro.max_attempts} attempts are used.</span></div>
                </div>

                <div className="flex gap-2 justify-end">
                  <button type="button" onClick={() => setAssessmentIntro(null)} disabled={!!starting} className="px-4 py-2 rounded-full text-stone font-bold">Cancel</button>
                  <button
                    data-testid="begin-assessment-button"
                    onClick={beginAssessment}
                    disabled={!!starting}
                    className="px-6 py-2.5 rounded-full bg-watermelon text-white font-bold btn-tactile hover:bg-watermelon-hover disabled:opacity-70 inline-flex items-center gap-2"
                  >
                    <Timer className="w-4 h-4" /> {starting ? "Starting…" : "Begin exam"}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
